import { useEffect, useState } from "react";
import { api } from "../lib/api";

/**
 * Composite status indicator.
 *
 *   SYNTHETIC DESIGN MODE          (always — design env never disappears)
 *     · KATLAS NODE ACTIVE         (only when /api/katlas/status -> status === "active")
 *     · KATLAS UNCONFIGURED        (status === "unconfigured")
 *     · KATLAS NODE UNREACHABLE    (any other error state)
 *
 * Honest by design: no fake live claims. Polls once on mount (cheap, no
 * polling loop). Token is never read here — only the public boolean
 * status field returned by the safe status endpoint.
 *
 * `tone="light"` renders dark-on-light (default).
 * `tone="dark"`  renders light-on-dark (used inside the hero panel).
 */
export const StatusPill = ({ tone = "light", testid = "status-pill", compact = false }) => {
  const [katlasStatus, setKatlasStatus] = useState("loading"); // loading | active | unconfigured | error

  useEffect(() => {
    let alive = true;
    api
      .get("/katlas/status")
      .then(({ data }) => {
        if (!alive) return;
        if (data?.status === "active" && data?.node_reachable === true) {
          setKatlasStatus("active");
        } else if (data?.status === "unconfigured") {
          setKatlasStatus("unconfigured");
        } else {
          setKatlasStatus("error");
        }
      })
      .catch(() => alive && setKatlasStatus("error"));
    return () => {
      alive = false;
    };
  }, []);

  const katlasLabel = {
    loading: compact ? "checking" : "KATLAS node · checking",
    active: compact ? "KATLAS active" : "KATLAS node active",
    unconfigured: compact ? "KATLAS unconfigured" : "KATLAS unconfigured",
    error: compact ? "KATLAS unreachable" : "KATLAS node unreachable",
  }[katlasStatus];

  const dotClass = {
    loading: "bg-muted-foreground",
    active: "bg-[#3FB37F]", // a single calm green for "live" — distinct from the now-retired emerald accents
    unconfigured: "bg-amber-500",
    error: "bg-rose-500",
  }[katlasStatus];

  const isDark = tone === "dark";
  const containerClass = isDark
    ? "border-white/15 bg-white/5 text-white/85"
    : "border-foreground/15 bg-white text-foreground";
  const dividerClass = isDark ? "bg-white/20" : "bg-foreground/15";
  const synthDot = isDark ? "bg-[#7FB2FF]" : "bg-[#0A2540]"; // KATLAS blue

  return (
    <div
      data-testid={testid}
      data-katlas-status={katlasStatus}
      className={`inline-flex items-center gap-3 border px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.18em] ${containerClass}`}
    >
      <span className="inline-flex items-center gap-2">
        <span className={`h-1.5 w-1.5 ${synthDot}`} />
        <span>{compact ? "Synthetic" : "Synthetic design mode"}</span>
      </span>
      <span className={`h-3 w-px ${dividerClass}`} aria-hidden="true" />
      <span className="inline-flex items-center gap-2">
        <span className="relative flex h-1.5 w-1.5">
          {katlasStatus === "active" && (
            <span className={`absolute inline-flex h-full w-full animate-ping rounded-full opacity-70 ${dotClass}`} />
          )}
          <span className={`relative inline-flex h-1.5 w-1.5 rounded-full ${dotClass}`} />
        </span>
        <span data-testid="status-pill-katlas-label">{katlasLabel}</span>
      </span>
    </div>
  );
};

export default StatusPill;
